import {
	Select,
	SelectItem,
	SelectOption
} from "client/Components";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import {
	RecurrencePatternKind
} from "../../common";
import { localize } from "client_legacy/ndaw/culture";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	AppointmentDayOfWeek,
	AppointmentWeekIndex
} from "client/Api/Calendar/calendar";
import { dayOfWeekToAppointmentDayOfWeekMap } from "./WeeklyCustomOptionSelector";
import {
	getDayOfMonth,
	getDayOfWeek,
	getFullMonthName,
	isValidDate
} from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { resolveFormattedTextString } from "client/Misc/utils";
import { formatDayOfWeek } from "client/DateAndTime/culture";
import { useEffectSkipFirstRender } from "client/Hooks";

export type RecurrencePatternKindSelectorProps = {
	attachable: IValueInputAttachable<RecurrencePatternKind>;
	startDate: Date;
	timezone: TimeZone;
	isDontRepeatDisabled: boolean;
	onChange: (kind: RecurrencePatternKind) => void;
};

export const RecurrencePatternKindSelector = (props: RecurrencePatternKindSelectorProps): ReactElement => {
	const {
		attachable,
		startDate,
		timezone,
		isDontRepeatDisabled,
		onChange
	} = props;

	const [validStartDate, setValidStartDate] = useState<Date>(
		() => isValidDate(startDate) ? startDate : new Date()
	);

	useEffectSkipFirstRender(
		(): void => {
			if (isValidDate(startDate)) {
				setValidStartDate(startDate);
			}
		},
		[startDate]
	);

	const options = useMemo(
		(): readonly SelectItem<RecurrencePatternKind>[] => {
			return getRecurrencePatternKindSelectorOptions(
				isDontRepeatDisabled,
				validStartDate,
				timezone
			);
		},
		[
			isDontRepeatDisabled,
			validStartDate,
			timezone
		]
	);

	return (
		<Select
			{...attachable
				.attachToSelect()
				.withOnChange(onChange)
			}
			menuTitle={localize("_Repeat")}
			options={options}
		/>
	);
};
RecurrencePatternKindSelector.displayName="RecurrencePatternKindSelector";

export function getRecurrencePatternKindSelectorOptions(
	isDontRepeatDisabled: boolean,
	startDate: Date,
	timezone: TimeZone
): readonly SelectOption<RecurrencePatternKind>[] {
	const dayOfMonth: number = getDayOfMonth(startDate, timezone);
	const appointmentWeekIndex: AppointmentWeekIndex = getWeekIndexInMonth(startDate, timezone);
	const formattedDayOfWeekName: string = formatDayOfWeek(
		getDayOfWeek(startDate, timezone),
		"full"
	);

	return [
		{
			text: localize("_DontRepeat"),
			value: RecurrencePatternKind.DontRepeat,
			isDisabled: isDontRepeatDisabled
		},
		{
			text: localize("_Daily"),
			value: RecurrencePatternKind.Daily,
		},
		{
			text: resolveFormattedTextString({
				template: localize("_WeeklyOnDay"),
				replacements: [
					{
						what: "{{Day}}",
						filler: formattedDayOfWeekName
					}
				]
			}),
			value: RecurrencePatternKind.Weekly,
		},
		{
			text: resolveFormattedTextString({
				template: localize("_MonthlyOnDayNumber"),
				replacements: [
					{
						what: "{{DayNumber}}",
						filler: dayOfMonth.toString()
					}
				]
			}),
			value: RecurrencePatternKind.AbsoluteMonthly,
		},
		{
			text: getRelativeMonthlyOptionText(
				appointmentWeekIndex,
				formattedDayOfWeekName
			),
			value: RecurrencePatternKind.RelativeMonthly,
		},
		...(appointmentWeekIndex === AppointmentWeekIndex.Fourth
			? [
				{
					text: getRelativeMonthlyOptionText(
						AppointmentWeekIndex.Last,
						formattedDayOfWeekName
					),
					value: RecurrencePatternKind.RelativeMonthlyLastWeek,
				}
			]
			: []
		),
		{
			text: resolveFormattedTextString({
				template: localize("_AnnuallyOnDayMonth"),
				replacements: [
					{
						what: "{{Day}}",
						filler: dayOfMonth.toString()
					},
					{
						what: "{{Month}}",
						filler: getFullMonthName(startDate, timezone)
					}
				]
			}),
			value: RecurrencePatternKind.Yearly
		},
		{
			text: localize("_Custom"),
			value: RecurrencePatternKind.Custom,
			groupName: "custom"
		},
	];
}

function getRelativeMonthlyOptionText(
	appointmentWeekIndex: AppointmentWeekIndex,
	formattedDayOfWeekName: string
): string {
	return resolveFormattedTextString({
		template: localize("_MonthlyOnWeekIndexAndDay"),
		replacements: [
			{
				what: "{{WeekIndex}}",
				filler: localize(
					weekIndexLocalizationKeyMap[appointmentWeekIndex]
				)
			},
			{
				what: "{{Day}}",
				filler: formattedDayOfWeekName
			}
		]
	});
}

export function getAppointmentDayOfWeek(
	date: Date,
	timeZone: TimeZone
): AppointmentDayOfWeek {
	return dayOfWeekToAppointmentDayOfWeekMap[getDayOfWeek(date, timeZone)];
}

export function getWeekIndexInMonth(
	date: Date,
	timezone: TimeZone
): AppointmentWeekIndex {
	const dayOfMonth: number = getDayOfMonth(date, timezone);
	const weekdayOccurrenceInMonth: number = Math.floor((dayOfMonth - 1) / 7);

	switch (weekdayOccurrenceInMonth) {
		case 0:
			return AppointmentWeekIndex.First;
		case 1:
			return AppointmentWeekIndex.Second;
		case 2:
			return AppointmentWeekIndex.Third;
		case 3:
			return AppointmentWeekIndex.Fourth;
		case 4:
			return AppointmentWeekIndex.Last;
		default:
			throw new Error(`Unexpected weekdayOccurrenceInMonth ${weekdayOccurrenceInMonth}`);
	}
}

export const weekIndexLocalizationKeyMap: {[key in AppointmentWeekIndex]: LocalizationKey } = {
	[AppointmentWeekIndex.First]: "_First",
	[AppointmentWeekIndex.Second]: "_Second",
	[AppointmentWeekIndex.Third]: "_Third",
	[AppointmentWeekIndex.Fourth]: "_Fourth",
	[AppointmentWeekIndex.Last]: "_Last",
};
